import {GET, POST} from 'helpers/services/constants/API_CONSTANTS';
import {
  API_GENERATE_REPORT_BYOC,
  API_EXPORT_REPORT_BYOC,
  API_GENERATE_REPORT_SELF_DELIVERY_SUMMARY,
  API_EXPORT_REPORT_SELF_DELIVERY_SUMMARY,
  API_GENERATE_REPORT_BOOK_A_DRIVER,
  API_EXPORT_REPORT_BOOK_A_DRIVER,
  API_GENERATE_REPORT_PAYOUT_SUMMARY,
  API_EXPORT_REPORT_PAYOUT_SUMMARY,
  API_GENERATE_REPORT_SALES_SUMMARY,
  API_EXPORT_REPORT_SALES_SUMMARY,
  API_BULK_EXPORT_SALES_SUMMARY,
  API_GENERATE_REPORT_REFUNDS_SUMMARY,
  API_GET_REPORTS_CONTENT_REFUNDS_SUMMARY,
  API_EXPORT_REPORT_REFUNDS_SUMMARY,
  API_GENERATE_REPORT_FEEDBACK_SUMMARY,
  API_GET_REPORTS_CONTENT_FEEDBACK_SUMMARY,
  API_EXPORT_REPORT_FEEDBACK_SUMMARY,
  API_GENERATE_REPORTS_CUSTOMERS,
  API_EXPORT_REPORT_CUSTOMERS,
  API_GET_SPECIAL_ORDERS_REPORT,
  API_ADD_USER_TO_SPECIAL_ORDERS_REPORT,
  API_GENERATE_REPORT_BAD_SUMMARY,
  API_EXPORT_REPORT_BAD_SUMMARY,
  API_GENERATE_REPORT_DISCOUNT_SUMMARY,
  API_EXPORT_REPORT_DISCOUNT_SUMMARY,
  API_GENERATE_REPORT_CASHBACK_USAGE,
  API_EXPORT_REPORT_CASHBACK_USAGE,
  API_GENERATE_REPORT_SALES_BREAKDOWN,
  API_EXPORT_REPORT_SALES_BREAKDOWN,
  API_GENERATE_REPORT_SELF_DELIVERY_INVOICES,
  API_EXPORT_SELF_DELIVERY_INVOICE_ORDERS_CSV,
  API_GET_SELF_DELIVERY_INVOICE_LINK
} from 'helpers/services/constants/API_ENDPOINTS';
import {request} from 'helpers/services/utils/request';
import {REPORT_TYPES} from 'helpers/constants/reports';

const unwrapApiResponse = (response) => {
  if (response?.data?.data !== undefined) {
    return {...response, data: response.data.data};
  }
  return response;
};

const generateReportApiEndpoints = {
  0: API_GENERATE_REPORT_SALES_SUMMARY,
  1: API_GENERATE_REPORTS_CUSTOMERS,
  2: API_GENERATE_REPORT_PAYOUT_SUMMARY,
  3: API_GENERATE_REPORT_SELF_DELIVERY_SUMMARY, // Self Delivery Summary report
  4: API_GENERATE_REPORT_BOOK_A_DRIVER,
  5: API_GENERATE_REPORT_REFUNDS_SUMMARY,
  6: API_GENERATE_REPORT_FEEDBACK_SUMMARY,
  8: API_GENERATE_REPORT_BAD_SUMMARY,
  9: API_GENERATE_REPORT_DISCOUNT_SUMMARY,
  10: API_GENERATE_REPORT_CASHBACK_USAGE,
  [REPORT_TYPES.SALES_BREAKDOWN_SUMMARY]: API_GENERATE_REPORT_SALES_BREAKDOWN,
  [REPORT_TYPES.SELF_DELIVERY_INVOICES]: API_GENERATE_REPORT_SELF_DELIVERY_INVOICES
};

const buildSalesBreakdownReportBody = (
  locationId,
  startDate,
  endDate,
  filters = {}
) => {
  const hasPeriod = Boolean(startDate || endDate);
  return {
    locationId,
    period: hasPeriod
      ? {
          from: startDate,
          to: endDate
        }
      : null,
    channels: filters.channels?.length ? filters.channels : null,
    fulfillments: filters.fulfillments?.length ? filters.fulfillments : null
  };
};

const buildReportRequestBody = (
  reportType,
  startDate,
  endDate,
  locationId,
  filters
) => {
  const hasPeriod = Boolean(startDate || endDate);
  if (reportType === REPORT_TYPES.SALES_BREAKDOWN_SUMMARY) {
    return buildSalesBreakdownReportBody(
      locationId,
      startDate,
      endDate,
      filters
    );
  }
  if (reportType === 10 && !hasPeriod) {
    return {locationId};
  }
  return {
    locationId,
    period: hasPeriod
      ? {
          from: startDate,
          to: endDate
        }
      : null
  };
};

// Self Delivery Summary only: which date the period is applied to (create and export)
const withSelfDeliveryDateFilter = (data, reportType, dateFilter) =>
  reportType === 3 && dateFilter ? {...data, dateFilter} : data;

export const generateReportApi = async (
  startDate,
  endDate,
  locationId,
  reportType,
  filters,
  dateFilter
) => {
  const data = withSelfDeliveryDateFilter(
    buildReportRequestBody(reportType, startDate, endDate, locationId, filters),
    reportType,
    dateFilter
  );

  const response = await request(POST, generateReportApiEndpoints[reportType], {
    data
  });
  return unwrapApiResponse(response);
};

const getReportsContentApiEndpoints = {
  // 3: API_GET_REPORTS_ORDERS_BYOC,
  // 4: API_GET_REPORTS_ORDERS_BOOK_A_DRIVER
  5: API_GET_REPORTS_CONTENT_REFUNDS_SUMMARY,
  6: API_GET_REPORTS_CONTENT_FEEDBACK_SUMMARY
};

export const getReportsApi = async (reportId, reportType, skip) => {
  return request(
    GET,
    getReportsContentApiEndpoints[reportType](reportId, skip)
  );
};

export const getSpecialOrdersReportApi = async (data) => {
  const response = await request(POST, API_GET_SPECIAL_ORDERS_REPORT(), {data});
  return unwrapApiResponse(response);
};

export const addEmailToSpecialOrdersReportApi = async (merchantId, email) => {
  return request(POST, API_ADD_USER_TO_SPECIAL_ORDERS_REPORT(merchantId), {
    data: `"${email}"`
  });
};

const exportApiEndpoints = {
  0: API_EXPORT_REPORT_SALES_SUMMARY,
  1: API_EXPORT_REPORT_CUSTOMERS,
  2: API_EXPORT_REPORT_PAYOUT_SUMMARY,
  3: API_EXPORT_REPORT_SELF_DELIVERY_SUMMARY, // Self Delivery Summary report
  4: API_EXPORT_REPORT_BOOK_A_DRIVER,
  5: API_EXPORT_REPORT_REFUNDS_SUMMARY,
  6: API_EXPORT_REPORT_FEEDBACK_SUMMARY,
  8: API_EXPORT_REPORT_BAD_SUMMARY,
  9: API_EXPORT_REPORT_DISCOUNT_SUMMARY,
  10: API_EXPORT_REPORT_CASHBACK_USAGE,
  [REPORT_TYPES.SALES_BREAKDOWN_SUMMARY]: API_EXPORT_REPORT_SALES_BREAKDOWN
};

export const getXlsReportsApi = async (
  reportId,
  reportType,
  locationId,
  dateRange,
  filters,
  dateFilter
) => {
  if (
    reportType === 3 ||
    reportType === 10 ||
    reportType === REPORT_TYPES.SALES_BREAKDOWN_SUMMARY
  ) {
    const data = withSelfDeliveryDateFilter(
      buildReportRequestBody(
        reportType,
        dateRange?.[0],
        dateRange?.[1],
        locationId,
        filters
      ),
      reportType,
      dateFilter
    );

    return request(
      POST,
      exportApiEndpoints[reportType],
      {
        data
      },
      {
        responseType: 'blob'
      }
    );
  }

  // For other report types, use old format with reportId
  return request(
    POST,
    exportApiEndpoints[reportType](reportId),
    {},
    {
      responseType: 'blob'
    }
  );
};

export const exportSelfDeliveryInvoiceOrdersCsvApi = async (
  locationId,
  stripeNumber,
  chargeDate
) => {
  return request(
    POST,
    API_EXPORT_SELF_DELIVERY_INVOICE_ORDERS_CSV,
    {data: {locationId, stripeNumber, chargeDate: chargeDate || null}},
    {responseType: 'blob'}
  );
};

export const getSelfDeliveryInvoiceLinkApi = async (
  locationId,
  stripeNumber
) => {
  const response = await request(POST, API_GET_SELF_DELIVERY_INVOICE_LINK, {
    data: {locationId, stripeNumber}
  });
  return unwrapApiResponse(response);
};

export const bulkExportSalesSummaryApi = async ({locationIds, period}) => {
  const response = await request(
    POST,
    API_BULK_EXPORT_SALES_SUMMARY,
    {data: {locationIds, period}},
    {silent: true}
  );
  return unwrapApiResponse(response)?.data;
};
